import { supabase, TABLES } from "../lib/supabase";
import type { Database } from "../pages/types/supabase";

type Worker = Database["public"]["Tables"]["workers"]["Row"];
type WorkerInsert = Database["public"]["Tables"]["workers"]["Insert"];
type WorkerUpdate = Database["public"]["Tables"]["workers"]["Update"];

export type WorkerFilterOption = Pick<Worker, "id" | "full_name">;

export interface WorkerWithRole extends Worker {
  active: boolean;
  roles: {
    role_name: string;
  };
  commission_levels?: {
    level_number: number;
    description: string;
  };
  teams?: Array<{
    id: number;
    name: string;
    description: string | null;
    active: boolean;
  }>;
}

export async function getWorkerIdForUser(userId: string): Promise<number> {
  const { data: worker, error } = await supabase
    .from(TABLES.WORKERS)
    .select("id")
    .eq("user_id", userId)
    .single();

  if (error) {
    throw new Error(`Failed to get worker ID: ${error.message}`);
  }

  if (!worker) {
    throw new Error("No worker found for current user");
  }

  return worker.id;
}

export async function getWorkers(): Promise<WorkerWithRole[]> {
  const { data, error } = await supabase
    .from(TABLES.WORKERS)
    .select(
      `
      *,
      roles:role_id (
        role_name
      ),
      commission_levels:commission_level_id (
        level_number,
        description
      ),
      worker_teams!worker_teams_worker_id_fkey (
        active,
        team_id,
        teams:team_id (
          id,
          name,
          description,
          active
        )
      )
    `
    )
    .order("full_name", { ascending: true });

  if (error) throw error;

  // Transform the data to group teams by worker
  const workersWithTeams = data?.map(worker => ({
    ...worker,
    teams: worker.worker_teams
      ?.filter((wt: any) => wt.active && wt.teams?.active)
      .map((wt: any) => wt.teams)
      .filter(Boolean) || []
  })) || [];

  return workersWithTeams;
}

export async function getActiveWorkerFilterOptions(): Promise<
  WorkerFilterOption[]
> {
  const { data, error } = await supabase
    .from(TABLES.WORKERS)
    .select("id, full_name")
    .eq("active", true)
    .order("full_name", { ascending: true });

  if (error) throw error;

  return data || [];
}

export async function getWorkerById(
  id: number
): Promise<WorkerWithRole | null> {
  const { data, error } = await supabase
    .from(TABLES.WORKERS)
    .select(
      `
      *,
      roles:role_id (
        role_name
      ),
      commission_levels:commission_level_id (
        level_number,
        description
      ),
      worker_teams!worker_teams_worker_id_fkey (
        active,
        team_id,
        teams:team_id (
          id,
          name,
          description,
          active
        )
      )
    `
    )
    .eq("id", id)
    .single();

  if (error) throw error;
  
  if (!data) return null;
  
  // Transform the data to group teams
  const workerWithTeams = {
    ...data,
    teams: data.worker_teams
      ?.filter((wt: any) => wt.active && wt.teams?.active)
      .map((wt: any) => wt.teams)
      .filter(Boolean) || []
  };

  return workerWithTeams;
}

export async function createWorker(worker: WorkerInsert): Promise<Worker> {
  const { data, error } = await supabase
    .from(TABLES.WORKERS)
    .insert(worker)
    .select("*")
    .single();

  if (error) throw error;
  if (!data) throw new Error("Failed to create worker");
  return data;
}

export async function updateWorker(
  id: number,
  worker: WorkerUpdate
): Promise<Worker> {
  const workerData = Object.fromEntries(
    Object.entries(worker).filter(([, value]) => typeof value !== "undefined")
  ) as Database["public"]["Functions"]["admin_update_worker"]["Args"]["p_worker_data"];

  const { data, error } = await supabase.rpc("admin_update_worker", {
    p_worker_id: id,
    p_worker_data: workerData,
  });

  if (error) throw error;
  if (!data) throw new Error("Worker not found");
  return data;
}

export async function deleteWorker(id: number): Promise<void> {
  const { error } = await supabase.from(TABLES.WORKERS).delete().eq("id", id);

  if (error) throw error;
}

// Enhanced worker-team management functions
export async function updateWorkerWithTeams(
  workerId: number,
  workerData: WorkerUpdate,
  teamIds: number[]
): Promise<WorkerWithRole> {
  try {
    // Start a transaction-like operation
    // First update the worker data
    await updateWorker(workerId, workerData);
    
    // Then update team assignments using the teams service
    const { updateWorkerTeams } = await import("./teams");
    await updateWorkerTeams(workerId, teamIds);
    
    // Fetch and return the updated worker with teams
    const workerWithTeams = await getWorkerById(workerId);
    if (!workerWithTeams) {
      throw new Error("Failed to fetch updated worker data");
    }
    
    return workerWithTeams;
  } catch (error) {
    console.error("Error updating worker with teams:", error);
    const message =
      error instanceof Error
        ? error.message
        : typeof error === "object" && error !== null && "message" in error
        ? String((error as { message: unknown }).message)
        : "Failed to update worker and team assignments";
    throw new Error(message);
  }
}

export async function getWorkerTeamIds(workerId: number): Promise<number[]> {
  try {
    const worker = await getWorkerById(workerId);
    return worker?.teams?.map(team => team.id) || [];
  } catch (error) {
    console.error("Error fetching worker team IDs:", error);
    return [];
  }
}

// Validate team assignments before updating
export async function validateTeamAssignments(teamIds: number[]): Promise<boolean> {
  if (teamIds.length === 0) return true;
  
  try {
    const { getAllTeams } = await import("./teams");
    const allTeams = await getAllTeams();
    const validTeamIds = allTeams.map(team => team.id);
    
    return teamIds.every(teamId => validTeamIds.includes(teamId));
  } catch (error) {
    console.error("Error validating team assignments:", error);
    return false;
  }
}

// Additional worker-specific functions
export async function getWorkersByRole(
  roleId: number
): Promise<WorkerWithRole[]> {
  const { data, error } = await supabase
    .from(TABLES.WORKERS)
    .select(
      `
      *,
      roles:role_id (
        role_name
      ),
      commission_levels:commission_level_id (
        level_number,
        description
      ),
      worker_teams!worker_teams_worker_id_fkey (
        active,
        team_id,
        teams:team_id (
          id,
          name,
          description,
          active
        )
      )
    `
    )
    .eq("role_id", roleId)
    .order("created_at", { ascending: false });

  if (error) throw error;

  // Transform the data to group teams by worker
  const workersWithTeams = data?.map(worker => ({
    ...worker,
    teams: worker.worker_teams
      ?.filter((wt: any) => wt.active && wt.teams?.active)
      .map((wt: any) => wt.teams)
      .filter(Boolean) || []
  })) || [];

  return workersWithTeams;
}

export async function getWorkersByManager(
  managerId: number
): Promise<WorkerWithRole[]> {
  const { data, error } = await supabase
    .from(TABLES.WORKERS)
    .select(
      `
      *,
      roles:role_id (
        role_name
      ),
      commission_levels:commission_level_id (
        level_number,
        description
      ),
      worker_teams!worker_teams_worker_id_fkey (
        active,
        team_id,
        teams:team_id (
          id,
          name,
          description,
          active
        )
      )
    `
    )
    .eq("manager_id", managerId)
    .order("created_at", { ascending: false });

  if (error) throw error;

  // Transform the data to group teams by worker
  const workersWithTeams = data?.map(worker => ({
    ...worker,
    teams: worker.worker_teams
      ?.filter((wt: any) => wt.active && wt.teams?.active)
      .map((wt: any) => wt.teams)
      .filter(Boolean) || []
  })) || [];

  return workersWithTeams;
}

export async function updateWorkerLocation(
  id: number,
  latitude: string,
  longitude: string
): Promise<Worker> {
  const { data, error } = await supabase
    .from(TABLES.WORKERS)
    .update({
      current_latitude: latitude,
      current_longitude: longitude,
      last_update: new Date().toISOString(), // Set to current timestamp
    })
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  if (!data) throw new Error("Worker not found");
  return data;
}

// Get aggregated goals globally or for an explicit, already-authorized scope.
export async function getAggregatedWorkerGoals(workerIds?: number[]): Promise<{
  daily: number;
  monthly: number;
}> {
  if (workerIds && workerIds.length === 0) {
    return { daily: 0, monthly: 0 };
  }

  let query = supabase
    .from(TABLES.WORKERS)
    .select("revenue_goal_daily, revenue_goal_month")
    .eq("active", true);

  if (workerIds) {
    query = query.in("id", workerIds);
  }

  const { data, error } = await query;

  if (error) throw error;

  const daily = data.reduce(
    (sum, worker) => sum + Number(worker.revenue_goal_daily || 0),
    0
  );
  const monthly = data.reduce(
    (sum, worker) => sum + Number(worker.revenue_goal_month || 0),
    0
  );

  return { daily, monthly };
}
